// MyRequests.js - List of user's feedback submissions
import React, { useState } from 'react';
import styled from 'styled-components';
import THEME from '../../styles/theme';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${THEME.spacing.md};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${THEME.spacing.xxl};
  color: ${THEME.textSecondary};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${THEME.spacing.xxl};
  color: ${THEME.textSecondary};
`;

const RequestItem = styled.div`
  background: ${THEME.inputBackground};
  border-radius: ${THEME.borderRadiusMedium};
  border: 1px solid ${props => props.$hasUpdate ? THEME.accentPrimary : 'transparent'};
  overflow: hidden;
`;

const RequestHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${THEME.spacing.md};
  padding: ${THEME.spacing.md};
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const TypeBadge = styled.span`
  font-size: ${THEME.fontSize.sm};
  padding: 2px 8px;
  border-radius: 4px;
  background: ${props => props.$type === 'bug' ? 'rgba(255, 165, 0, 0.2)' : 'rgba(33, 150, 243, 0.2)'};
  color: ${props => props.$type === 'bug' ? THEME.error : THEME.info};
`;

const RequestTitle = styled.div`
  flex: 1;
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.md};
`;

const StatusBadge = styled.span`
  font-size: ${THEME.fontSize.xs};
  padding: 2px 8px;
  border-radius: 4px;
  background: ${props => {
    switch (props.$status) {
      case 'completed': return 'rgba(76, 175, 80, 0.2)';
      case 'in_progress': return 'rgba(33, 150, 243, 0.2)';
      case 'acknowledged': return 'rgba(156, 39, 176, 0.2)';
      case 'wont_fix': return 'rgba(158, 158, 158, 0.2)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'completed': return THEME.success;
      case 'in_progress': return THEME.info;
      case 'acknowledged': return '#9C27B0';
      case 'wont_fix': return THEME.textMuted;
      default: return THEME.textSecondary;
    }
  }};
  text-transform: capitalize;
`;

const UpdateDot = styled.span`
  width: 8px;
  height: 8px;
  background: ${THEME.accentPrimary};
  border-radius: 50%;
`;

const ExpandIcon = styled.span`
  color: ${THEME.textMuted};
  font-size: ${THEME.fontSize.sm};
  transition: transform ${THEME.transitionFast};
  transform: rotate(${props => props.$expanded ? '180deg' : '0deg'});
`;

const RequestDetails = styled.div`
  padding: 0 ${THEME.spacing.md} ${THEME.spacing.md};
  border-top: 1px solid ${THEME.inputBorder};
  display: ${props => props.$expanded ? 'block' : 'none'};
`;

const DetailSection = styled.div`
  margin-top: ${THEME.spacing.md};
`;

const DetailLabel = styled.div`
  color: ${THEME.textMuted};
  font-size: ${THEME.fontSize.xs};
  margin-bottom: ${THEME.spacing.xs};
  text-transform: uppercase;
`;

const DetailText = styled.div`
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.sm};
  line-height: 1.5;
  white-space: pre-wrap;
`;

const ResponseBox = styled.div`
  background: rgba(33, 150, 243, 0.1);
  border-left: 3px solid ${THEME.info};
  padding: ${THEME.spacing.md};
  border-radius: 0 ${THEME.borderRadiusSmall} ${THEME.borderRadiusSmall} 0;
  margin-top: ${THEME.spacing.md};
`;

const ResponseLabel = styled.div`
  color: ${THEME.info};
  font-size: ${THEME.fontSize.xs};
  font-weight: 500;
  margin-bottom: ${THEME.spacing.xs};
`;

const DateText = styled.div`
  color: ${THEME.textMuted};
  font-size: ${THEME.fontSize.xs};
  margin-top: ${THEME.spacing.sm};
`;

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatStatus = (status) => {
  return status.replace('_', ' ');
};

const MyRequests = ({ requests, loading, onRefresh }) => {
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return <LoadingState>Loading your requests...</LoadingState>;
  }

  if (requests.length === 0) {
    return (
      <EmptyState>
        You haven't submitted any feedback yet.
        <br />
        Use the "Report Bug" or "Request Feature" tabs to get started!
      </EmptyState>
    );
  }

  return (
    <Container>
      {requests.map(request => (
        <RequestItem key={request.id} $hasUpdate={request.hasUpdate}>
          <RequestHeader onClick={() => toggleExpand(request.id)}>
            <TypeBadge $type={request.type}>
              {request.type === 'bug' ? 'Bug' : 'Feature'}
            </TypeBadge>
            <RequestTitle>{request.title}</RequestTitle>
            <StatusBadge $status={request.status}>
              {formatStatus(request.status)}
            </StatusBadge>
            {request.hasUpdate && <UpdateDot />}
            <ExpandIcon $expanded={expanded[request.id]}>▼</ExpandIcon>
          </RequestHeader>

          <RequestDetails $expanded={expanded[request.id]}>
            <DetailSection>
              <DetailLabel>Description</DetailLabel>
              <DetailText>{request.description}</DetailText>
            </DetailSection>

            {request.area && (
              <DetailSection>
                <DetailLabel>Area</DetailLabel>
                <DetailText>{request.area}</DetailText>
              </DetailSection>
            )}

            {request.admin_response && (
              <ResponseBox>
                <ResponseLabel>Developer Response</ResponseLabel>
                <DetailText>{request.admin_response}</DetailText>
              </ResponseBox>
            )}

            <DateText>
              Submitted: {formatDate(request.created_at)}
              {request.completed_at && (
                <> | Completed: {formatDate(request.completed_at)}</>
              )}
            </DateText>
          </RequestDetails>
        </RequestItem>
      ))}
    </Container>
  );
};

export default MyRequests;
